import Button from '@app/react-common/components/button/Button';
import { ExpandableText } from '@app/react-common/components/expandable-text/ExpandableText';
import StatusText from '@app/react-common/components/status-text/StatusText';
import ThreeDotMenu, { ThreeDotMenuItem } from '@app/react-common/components/three-dot-menu/ThreeDotMenu';
import { ToggleButton } from '@app/react-common/components/toggle-button/ToggleButton';
import React, { useMemo, useState } from 'react';
import { Shippings } from '@app/pages/checkout-app/types';
import { LocationSettingsExpandableSettings } from '../delivery-methods-list/location-settings-expandable-text/LocationSettingsExpandableSettings';
import { DisplayRatesComponent } from '../delivery-methods-list/display-rates/DisplayRatesComponent';
import { useAppContext } from '@app/react-common/AppContext';
import { Checkout } from '../../models/Checkout';
import { ConfirmationModal } from '@app/react-common/components/confirmation-modal/ConfirmationModal';
import { rateHasAnyDiscount } from '@app/pages/checkout-app/utils';
import Tooltip from '@app/react-common/components/tooltip/Tooltip';
import {
  getDeliveryMethodsDaysNTimes,
  getDisplayEstimatedDeliveryText,
  getEstimatedTimeText,
  isLiveRateMethod,
} from '../delivery-methods-list/utils/DeliveryMethodUtils';
import { ICheckoutActions } from './../../models/ICheckoutActions';
import { DuplicateMethodToAnotherCheckoutGroupModal } from '../delivery-methods-list/duplicate-method-modal/DuplicateMethodToAnotherCheckoutGroupModal';
import { Checkbox } from '@app/react-common/components/checkbox/Checkbox';
import {
  DeliveryMethodsFilterBar,
  getCurrencySign,
  displayCondition,
  formatCategoryDisplay,
} from '../delivery-methods-list/components';
import { BulkEditModal, ConditionInfo } from '../delivery-methods-list/bulk-edit-modal/BulkEditModal';
import { useDeliveryMethodsState } from '../delivery-methods-list/hooks/useDeliveryMethodsState';
import { useAllShippingMethods } from '../delivery-methods-list/hooks/useAllShippingMethods';
import { mapShippingToSelectedMethodInfo } from '../delivery-methods-list/utils/mapToSelectedMethodInfo';
import { MIN_METHODS_FOR_BULK_EDIT } from '../delivery-methods-list/utils/consts';
import {
  buildDeliveryMethodDisplayOrder,
  getMethodIdsInDisplayOrder,
} from '../delivery-methods-list/sections/deliveryMethodDisplayOrder';

interface AllGroupsDeliveryMethodsListProps {
  checkoutGroups: Checkout[];
  checkoutActions: ICheckoutActions;
  onDataRefresh?: () => void;
  onInvalidateGroups?: (groupIds: number[]) => void;
  refetchGroupShippings?: (groupId: number) => Promise<void>;
  isLoadingGroups?: boolean;
}

interface GroupedDeliveryMethods {
  group: Checkout;
  deliveryMethods: Shippings[];
}

export const AllGroupsDeliveryMethodsList = (props: AllGroupsDeliveryMethodsListProps) => {
  const [deleteDeliveryMethod, setDeleteDeliveryMethod] = useState<{
    id: string;
    name: string;
    groupId: number;
  } | null>(null);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [deliveryMethodToDuplicate, setDeliveryMethodToDuplicate] = useState<{
    id: number;
    name: string;
    groupId: number;
  } | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());

  const { angularRedirect, shop$ } = useAppContext();

  const {
    shopCurrency,
    shopCurrencyCode,
    shopCountryCode,
    selectedIds,
    setSelectedIds,
    searchQuery,
    setSearchQuery,
    typeFilter,
    setTypeFilter,
    statusFilter,
    setStatusFilter,
    bulkEditModalOpen,
    setBulkEditModalOpen,
    handleSelectItem,
    filterMethod,
  } = useDeliveryMethodsState(shop$);

  const groupedDeliveryMethods: GroupedDeliveryMethods[] = useMemo(() => {
    return props.checkoutGroups.map((group) => {
      const orderableDeliveryMethods = group.checkoutShippings.filter((x) => !x.fallbackMethod);
      const deliveryMethodById = new Map(orderableDeliveryMethods.map((method) => [String(method.id), method]));
      const displayOrder = buildDeliveryMethodDisplayOrder(
        orderableDeliveryMethods,
        group.deliveryMethodSections ?? [],
      );
      return {
        group,
        deliveryMethods: getMethodIdsInDisplayOrder(displayOrder).map((methodId) => deliveryMethodById.get(methodId)),
      };
    });
  }, [props.checkoutGroups]);

  const allShippingMethods = useAllShippingMethods(props.checkoutGroups);

  const filteredGroupedDeliveryMethods: GroupedDeliveryMethods[] = useMemo(() => {
    return groupedDeliveryMethods
      .map((groupData) => ({
        group: groupData.group,
        deliveryMethods: groupData.deliveryMethods.filter(filterMethod),
      }))
      .filter((groupData) => groupData.deliveryMethods.length > 0);
  }, [groupedDeliveryMethods, filterMethod]);

  const allFilteredMethods = useMemo(() => {
    return filteredGroupedDeliveryMethods.flatMap((g) => g.deliveryMethods);
  }, [filteredGroupedDeliveryMethods]);

  const allFilteredSelected =
    allFilteredMethods.length > 0 && allFilteredMethods.every((method) => selectedIds.has(method.id));

  const selectedMethodsInfo = useMemo(() => {
    const result: ReturnType<typeof mapShippingToSelectedMethodInfo>[] = [];
    filteredGroupedDeliveryMethods.forEach((groupData) => {
      groupData.deliveryMethods
        .filter((method) => selectedIds.has(method.id))
        .forEach((method) => {
          result.push(mapShippingToSelectedMethodInfo(method, groupData.group.name || '', shopCurrencyCode));
        });
    });
    return result;
  }, [filteredGroupedDeliveryMethods, selectedIds, shopCurrencyCode]);

  const isGroupAllSelected = (groupMethods: Shippings[]) => {
    return groupMethods.length > 0 && groupMethods.every((method) => selectedIds.has(method.id));
  };

  const isGroupSomeSelected = (groupMethods: Shippings[]) => {
    return groupMethods.some((method) => selectedIds.has(method.id)) && !isGroupAllSelected(groupMethods);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const newSelectedIds = new Set(selectedIds);
      allFilteredMethods.forEach((method) => newSelectedIds.add(method.id));
      setSelectedIds(newSelectedIds);
    } else {
      const newSelectedIds = new Set(selectedIds);
      allFilteredMethods.forEach((method) => newSelectedIds.delete(method.id));
      setSelectedIds(newSelectedIds);
    }
  };

  const handleSelectGroup = (groupMethods: Shippings[], checked: boolean) => {
    const newSelectedIds = new Set(selectedIds);
    if (checked) {
      groupMethods.forEach((method) => newSelectedIds.add(method.id));
    } else {
      groupMethods.forEach((method) => newSelectedIds.delete(method.id));
    }
    setSelectedIds(newSelectedIds);
  };

  const toggleGroup = (groupId: number) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  return (
    <div className="all-groups-delivery-methods-list">
      <div className="all-groups-delivery-methods-list__header">
        <h1>All Groups</h1>
        <div className="all-groups-delivery-methods-list__header__actions">
          <Button
            text={allFilteredSelected ? 'Deselect All' : 'Select All'}
            onClick={() => handleSelectAll(!allFilteredSelected)}
            size="medium"
            variant="tertiary"
            disabled={!allFilteredMethods.length}
          />
          {selectedIds.size < MIN_METHODS_FOR_BULK_EDIT || props.isLoadingGroups ? (
            <Tooltip
              tooltipText={
                props.isLoadingGroups
                  ? 'Loading delivery methods...'
                  : 'Select at least 2 delivery methods to bulk edit'
              }>
              <Button
                text={
                  props.isLoadingGroups
                    ? 'Loading...'
                    : selectedIds.size > 0
                      ? `Bulk Edit (${selectedIds.size})`
                      : 'Bulk Edit'
                }
                onClick={() => {}}
                size="medium"
                variant="primary"
                disabled={true}
              />
            </Tooltip>
          ) : (
            <Button
              text={`Bulk Edit (${selectedIds.size})`}
              onClick={() => setBulkEditModalOpen(true)}
              size="medium"
              variant="primary"
            />
          )}
        </div>
      </div>
      <DeliveryMethodsFilterBar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        typeFilter={typeFilter}
        onTypeFilterChange={setTypeFilter}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        className="all-groups-delivery-methods-list"
      />

      {filteredGroupedDeliveryMethods.map((groupData) => (
        <div key={groupData.group.id} className="all-groups-delivery-methods-list__group">
          <div
            className="all-groups-delivery-methods-list__group__header"
            onClick={() => toggleGroup(groupData.group.id)}>
            <i className={`icon icon-arrow_down ${!expandedGroups.has(groupData.group.id) ? 'collapsed' : ''}`} />
            <div onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={isGroupAllSelected(groupData.deliveryMethods)}
                indeterminate={isGroupSomeSelected(groupData.deliveryMethods)}
                onChange={(checked: boolean) => handleSelectGroup(groupData.deliveryMethods, checked)}
              />
            </div>
            <span className="all-groups-delivery-methods-list__group__header__name">{groupData.group.name}</span>
            <span className="all-groups-delivery-methods-list__group__header__count">
              ({groupData.deliveryMethods.length})
            </span>
          </div>
          {expandedGroups.has(groupData.group.id) && (
            <>
              <div className="all-groups-delivery-methods-list__group__table-header">
                <div className="all-groups-delivery-methods-list__table-column all-groups-delivery-methods-list__table-column--checkbox" />
                <div className="all-groups-delivery-methods-list__table-column">Internal name</div>
                <div className="all-groups-delivery-methods-list__table-column">Name shown in checkout</div>
                <div className="all-groups-delivery-methods-list__table-column">Display conditions</div>
                <div className="all-groups-delivery-methods-list__table-column">Rate</div>
              </div>
              <div className="all-groups-delivery-methods-list__group__methods">
                {groupData.deliveryMethods.map((deliveryMethod) => {
                  const conditionsGroup = (deliveryMethod.conditions as ConditionInfo[])
                    .filter((x: ConditionInfo) => x.name != 'locations')
                    .reduce(
                      (groups: Record<string, ConditionInfo[]>, item: ConditionInfo) => {
                        (groups[item.subsetGroupId] = groups[item.subsetGroupId] || []).push(item);
                        return groups;
                      },
                      {} as Record<string, ConditionInfo[]>,
                    );
                  const conditions = Object.values(conditionsGroup);
                  const isSelected = selectedIds.has(deliveryMethod.id);

                  return (
                    <div key={deliveryMethod.id} className="all-groups-delivery-methods-list__group__methods__row">
                      <div className="all-groups-delivery-methods-list__table-column all-groups-delivery-methods-list__table-column--checkbox all-groups-delivery-methods-list__group__methods__col">
                        <Checkbox
                          checked={isSelected}
                          onChange={(checked: boolean) => handleSelectItem(deliveryMethod.id, checked)}
                        />
                      </div>
                      <div className="all-groups-delivery-methods-list__table-column all-groups-delivery-methods-list__group__methods__col">
                        <p className="gray-text-small">{deliveryMethod.internalName || '-'}</p>
                        <div className="all-groups-delivery-methods-list__tags">
                          <StatusText
                            label={formatCategoryDisplay(deliveryMethod.shippingMethod.category)}
                            color={'primary'}
                          />
                          {deliveryMethod.isTestMode && <StatusText label={'Test mode'} color={'hyperlight20'} />}
                          {deliveryMethod.runningTest && <StatusText label={'A/B test'} color={'hyperlight20'} />}
                        </div>
                      </div>
                      <div className="all-groups-delivery-methods-list__table-column all-groups-delivery-methods-list__group__methods__col">
                        <h4>{deliveryMethod.name || '-'}</h4>
                        <div>{deliveryMethod.notes}</div>
                      </div>
                      <div className="all-groups-delivery-methods-list__table-column all-groups-delivery-methods-list__group__methods__col">
                        {conditions.length > 0 && (
                          <ExpandableText
                            title={'conditions'}
                            rows={conditions.map((group: ConditionInfo[]) => {
                              return group.map((x: ConditionInfo) =>
                                displayCondition(
                                  x,
                                  deliveryMethod.methodCurrency
                                    ? getCurrencySign(deliveryMethod.methodCurrency)
                                    : shopCurrency,
                                ),
                              );
                            })}
                            isGroups={true}
                          />
                        )}
                        {(deliveryMethod.displayEstimatedDeliveryBusinessDays || deliveryMethod.displayEstDelivery) && (
                          <ExpandableText
                            title={'delivery promise'}
                            rows={[
                              `${
                                isLiveRateMethod(deliveryMethod.shippingMethod.category)
                                  ? 'Processing time'
                                  : 'Delivery promise settings'
                              }`,
                              ...getEstimatedTimeText(deliveryMethod.estimatedTimeConversions),
                              ...getDisplayEstimatedDeliveryText(deliveryMethod),
                            ]}
                          />
                        )}
                        <LocationSettingsExpandableSettings shipping={deliveryMethod} />
                        <ExpandableText title={'delivery time'} rows={getDeliveryMethodsDaysNTimes(deliveryMethod)} />
                      </div>
                      <div className="all-groups-delivery-methods-list__table-column-small">
                        <DisplayRatesComponent shipping={deliveryMethod} />
                      </div>
                      <div className="all-groups-delivery-methods-list__group__methods__actions">
                        <ToggleButton
                          checked={deliveryMethod.enabled}
                          onChange={async () => {
                            props.checkoutActions.toggleEnableDeliveryMethod(
                              parseInt(deliveryMethod.id),
                              !deliveryMethod.enabled,
                              () => {},
                            );
                          }}
                        />
                        <ThreeDotMenu
                          className="all-groups-delivery-methods-list__group__methods__actions__three-dot"
                          items={getDeliveryMethodActions(
                            angularRedirect,
                            groupData.group,
                            deliveryMethod,
                            setDeleteDeliveryMethod,
                            setDeliveryMethodToDuplicate,
                            setDuplicateModalOpen,
                            props.checkoutGroups,
                          )}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      ))}

      <ConfirmationModal
        title={'Are you sure?'}
        buttonText={'Delete'}
        mainContent={`You're deleting the delivery method ${deleteDeliveryMethod?.name}`}
        isOpen={deleteDeliveryMethod != null}
        onClose={() => {
          setDeleteDeliveryMethod(null);
        }}
        onSuccess={async () => {
          if (deleteDeliveryMethod) {
            await props.checkoutActions.deleteDeliveryMethod(parseInt(deleteDeliveryMethod.id));
          }
          setDeleteDeliveryMethod(null);
        }}
      />

      {deliveryMethodToDuplicate && (
        <DuplicateMethodToAnotherCheckoutGroupModal
          checkouts={props.checkoutGroups.filter((checkout) => checkout.id !== deliveryMethodToDuplicate.groupId)}
          isOpen={duplicateModalOpen}
          onClose={() => {
            setDuplicateModalOpen(false);
          }}
          onSuccess={async (selectedIds: number[]) => {
            props.checkoutActions.duplicateMethodToOtherCheckoutGroups(deliveryMethodToDuplicate.id, selectedIds);
            setDuplicateModalOpen(false);
          }}
          deliveryMethodName={deliveryMethodToDuplicate.name || ''}
        />
      )}

      <BulkEditModal
        isOpen={bulkEditModalOpen}
        selectedCount={selectedIds.size}
        onClose={() => setBulkEditModalOpen(false)}
        onSaveComplete={(affectedGroupIds) => {
          setSelectedIds(new Set());
          if (affectedGroupIds?.length) {
            if (props.onInvalidateGroups) {
              props.onInvalidateGroups(affectedGroupIds);
            }
            affectedGroupIds.forEach((groupId) => {
              props.refetchGroupShippings?.(groupId);
            });
          }
        }}
        onDeselectMethods={(methodIds) => {
          const newSelectedIds = new Set(selectedIds);
          methodIds.forEach((id) => newSelectedIds.delete(id));
          setSelectedIds(newSelectedIds);
        }}
        onRestoreMethods={(methodIds) => {
          setSelectedIds(new Set(methodIds));
        }}
        currencySign={shopCurrency}
        selectedMethods={selectedMethodsInfo}
        shopCountryCode={shopCountryCode}
        allShippingMethods={allShippingMethods}
      />
    </div>
  );
};

const getDeliveryMethodActions = (
  angularRedirect: (url: string) => void,
  activeCheckout: Checkout,
  deliveryMethod: Shippings,
  setDeleteDeliveryMethod: React.Dispatch<React.SetStateAction<{ id: string; name: string; groupId: number } | null>>,
  setDeliveryMethodToDuplicate: React.Dispatch<
    React.SetStateAction<{ id: number; name: string; groupId: number } | null>
  >,
  setDuplicateModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
  allCheckoutGroups: Checkout[],
): ThreeDotMenuItem[] => {
  const actions: ThreeDotMenuItem[] = [
    {
      label: 'Edit',
      onClick: () => {
        angularRedirect(`/convert/checkout-groups/methods/edit?checkoutId=${deliveryMethod.id}`);
      },
    },
  ];
  actions.push({
    label: 'Duplicate',
    onClick: () => {
      angularRedirect(
        `/convert/checkout-groups/methods/new?did=${deliveryMethod.id}&collectionId=${activeCheckout.id}`,
      );
    },
  });

  if (!rateHasAnyDiscount(deliveryMethod) && allCheckoutGroups.length > 1) {
    actions.push({
      label: 'Duplicate to another group',
      onClick: () => {
        setDeliveryMethodToDuplicate({
          id: parseInt(deliveryMethod.id),
          name: deliveryMethod.name,
          groupId: activeCheckout.id,
        });
        setDuplicateModalOpen(true);
      },
    });
  }

  actions.push({
    label: 'Delete',
    onClick: () => {
      setDeleteDeliveryMethod({ id: deliveryMethod.id, name: deliveryMethod.name, groupId: activeCheckout.id });
    },
  });
  return actions;
};
